import { useState, useEffect, useRef } from 'react';
import TranslationText from '@Components/Utilities/TranslationText';
import { sandhillsRequest, getClientWidth } from '@Components/Utilities/SandhillsUtility';
import { rentalFacetOptions, leaseFacetOptions } from '@Components/Utilities/SandhillsConstants';
import ready from 'document-ready';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import ConditionalWrapper from '@Components/Utilities/ConditionalWrapper';

const EmployeeKeywordSearchParamsModal = loadable(() => import('@Components/Shared/Modals/EmployeeKeywordSearchParamsModal'));
const FacetedSearchCheckboxField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchCheckboxField'));
const FacetedSearchDateRangeField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchDateRangeField'));
const FacetedSearchRangeField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchRangeField'));
const FacetedSearchTextField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchTextField'));
const FacetedSearchRangeComboField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchRangeComboField'));
const FacetedSearchHybridField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchHybridField'));
const FacetedSearchDistanceField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchDistanceField'));

const FacetedSearchField = (props) => {
    const [showEmployeeKeywordModal, setShowEmployeeKeywordModal] = useState(false);
    const isShortListCount = props.FacetClosed ? 0 : props.FieldName == "InventoryType" || props.FieldName == "WinningBidderStatus" || props.SegmentOverhaul ? 20 : 5;
    const [isLoaded, setIsLoaded] = useState(!props.LoadAsync || props.OpenShowAllModalOnLoad);
    const [isOpen, setIsOpen] = useState((props.OpenFieldOnLoad && !props.IsEmptySearch) || props.SegmentOverhaul);
    const [options, setOptions] = useState(props.Options);
    const [allOptions, setAllOptions] = useState([]);
    const [filterOptions, setFilterOptions] = useState(props.FilterOptionsBySelections);
    const [rangeInfo, setRangeInfo] = useState(props.RangeInfo);
    const isCheckbox = props.Type === 'Checkbox';
    const returnFocusRef = useRef(null);
    const windowSize = useWindowSize();

    useEffect(function updateFacetedSearchField() {
        let facetIsOpen = isOpen && !props.IsEmptySearch;
        if (!(props.ShowModal && isCheckbox)) {
            setIsLoaded(false);
            setOptions(props.Options);
        }
        if (props.Type === 'Range' || props.Type === 'RangeCombo') {
            facetIsOpen = false;
            setIsLoaded(false);
            setRangeInfo(props.RangeInfo);
        }
        if (props.OpenFieldOnLoad !== facetIsOpen && !props.IsEmptySearch) {
            if (!props.ShowMobileFilters || !props.Collapsible) {
                facetIsOpen = props.OpenFieldOnLoad;
            }
        }
        setIsOpen(facetIsOpen);
        if (props.IsNextUp) {
            ready(() => {
                openShowAllModal(false, false, props.Options, [], props.FilterOptionsBySelections);
            });
        }
    }, [props.Options, props.RangeInfo, props.OpenFieldOnLoad, props.IsNextUp, props.IsEmptySearch]);

    useEffect(function toggleFacetedSearchField() {
        ready(() => {
            if (props.OpenShowAllModalOnLoad) {
                loadOptions(options, allOptions, filterOptions);
            } else if (window.innerWidth <= 785 && isOpen && !isCheckbox) {
                toggleOpenClose(true);
            }
        });
    }, []);

    const toggleEmployeeModal = () => {
        setShowEmployeeKeywordModal(!showEmployeeKeywordModal);
    };

    const runFacetedSearch = (latestFieldValue, nextFacetToLoad) => {
        props.RunSearch(false, latestFieldValue, nextFacetToLoad);
    };

    const toggleOpenClose = (isOnLoad) => {
        if (!props.IsEmptySearch) {
            if (props.Collapsible) {
                if ((props.MobileSearch || props.FacetClosed) && isCheckbox) {
                    openShowAllModal(isLoaded, isOnLoad, options, allOptions, filterOptions);
                } else if (isCheckbox) {
                    let callback = () => { };
                    let shouldExpand = options.length === 0 ? false : !isOpen;
                    if (!props.OpenFieldOnLoad) {
                        if (!isLoaded) {
                            callback = () => loadAllOptionsForFacet(false, props.ShowModal, options, allOptions, filterOptions);
                        }
                        if (!props.ShowModal && (options.length > isShortListCount || props.SegmentOverhaul)) {
                            callback = () => openShowAllModal(isLoaded, isOnLoad, options, allOptions, filterOptions);
                            shouldExpand = false;
                        }
                    }
                    setIsOpen(shouldExpand);
                    callback();
                } else {
                    if (!isOnLoad && props.onFacetOpen) {
                        props.onFacetOpen();
                    }
                    setIsOpen(isOpen => !isOpen);
                }
            }
        }
    };

    const renderFields = () => {
        switch (props.Type) {
            case 'Checkbox':
                return (
                    <FacetedSearchCheckboxField
                        {...props}
                        Options={options}
                        RunSearch={runFacetedSearch}
                        OpenShowAllModal={(e) => openShowAllModal(isLoaded, false, options, allOptions, filterOptions, e)}
                        IsShortListCount={isShortListCount}
                        SegmentOverhaul={props.SegmentOverhaul}
                        SegmentOptions={props.SegmentOptions}
                        ref={returnFocusRef}
                    />
                );
            case 'DateRange':
                return (
                    <FacetedSearchDateRangeField
                        {...props}
                        RunSearch={runFacetedSearch}
                    />
                );
            case 'Range':
                return (
                    <FacetedSearchRangeField
                        {...props}
                        RunSearch={runFacetedSearch}
                        RangeInfo={rangeInfo}
                    />
                );
            case 'RangeCombo':
                return (
                    <FacetedSearchRangeComboField
                        {...props}
                        RunSearch={runFacetedSearch}
                        RangeInfo={rangeInfo}
                    />
                );
            case 'Text':
                return (
                    <FacetedSearchTextField
                        {...props}
                        RunSearch={runFacetedSearch}
                    />
                );
            case 'Hybrid':
                return (
                    <FacetedSearchHybridField
                        {...props}
                        Options={options}
                        RunSearch={runFacetedSearch}
                    />
                );
            case 'Distance':
                return (
                    <FacetedSearchDistanceField
                        {...props}
                        RunSearch={runFacetedSearch}
                    />
                );
        }
    };

    const displayAppliedText = () => {
        if (props.DisplayAppliedText) {
            let queryStringRegex = new RegExp('(\\?|&)' + props.FieldName + '=', 'gi');
            if (props.HasOptionsSelected || (options && options.some(option => option.Checked)) || queryStringRegex.test(props.SearchURL)) {
                return getAppliedText();
            } else if (props.ParentNameForSearch && props.ParentNameForSearch != '') {
                queryStringRegex = new RegExp('(\\?|&)' + props.ParentNameForSearch + '=', 'gi');
                if (queryStringRegex.test(props.SearchURL)) {
                    return getAppliedText();
                }
            }
            else if (props.SpecLandingTermGroup) {
                queryStringRegex = new RegExp('(\\?|&)' + props.SpecLandingTermGroup + '=', 'gi');
                if (queryStringRegex.test(props.SearchURL)) {
                    return getAppliedText();
                }
            } else if ((props.FieldName == 'DailyRentalRateMin') && rentalFacetOptions.filter(c => props.SearchURL.includes(c)).length > 0) {
                return getAppliedText();
            } else if ((props.FieldName == 'LeasePrice') && leaseFacetOptions.filter(c => props.SearchURL.includes(c)).length > 0) {
                return getAppliedText();
            }
        }
    };

    const getAppliedText = () => {
        return (
            <span style={{ fontWeight: 'normal', fontSize: 'smaller' }}> - <TranslationText tKey='sandhills.applied' /></span>
        );
    };

    const getIconClass = () => {
        if ((props.MobileSearch && isCheckbox && !props.HasSingleSelectionOption) || !isOpen) {
            return 'fas fa-chevron-right';
        } else {
            return 'fas fa-minus';
        }
    };

    const isAriaExpanded = () => {
        if ((props.MobileSearch && props.Type === 'Checkbox') || !isOpen) {
            return false;
        }

        return true;
    };

    const getFacetedSectionBoxClass = () => {
        let className = 'faceted-section-box';
        if (((props.MobileSearch || props.FacetClosed) && isCheckbox) && !props.SegmentOverhaul || !isOpen && !props.SegmentOverhaul) {
            className += ' closed';
        }
        if (((props.MobileSearch || props.FacetClosed) && isCheckbox && !props.SegmentOverhaul) || (!isOpen && !props.OpenFieldOnLoad && isCheckbox && !props.SegmentOverhaul)) {
            className += ' showAllOnly';
        }
        return className;
    };

    const openShowAllModal = (isLoaded, isOnLoad, facets, allFacets, filter, segmentValue) => {
        if (!props.IsEmptySearch) {
            if (!isOnLoad && props.onFacetOpen && !segmentValue) {
                props.onFacetOpen();
            }
            if (getClientWidth() <= 785) {
                let dealerSearchWindow = document.getElementsByClassName('facets-toggle')?.[0];
                if (dealerSearchWindow) {
                    dealerSearchWindow.style.overflow = 'hidden';
                }
            }
            props.SetShowModal(true);
            loadAllOptionsForFacet(isLoaded, true, facets, allFacets, filter, segmentValue);
        }
    };

    const closeShowAllModal = (applyFilter, resetOptions, facets) => {
        if (getClientWidth() <= 785) {
            let dealerSearchWindow = document.getElementsByClassName('facets-toggle')?.[0];
            if (dealerSearchWindow) {
                dealerSearchWindow.style.overflow = 'auto';
            }
        }
        if (applyFilter) {
            if (props.setShowLoadingRing) props.setShowLoadingRing(true);
            window.scrollTo(0, 0);
        }
        if (resetOptions && facets?.length > 0) {
            setOptions(facets);
        }
        props.SetShowModal(false);
        props.DispatchFacetedSearchModal({
            type: 'destroy',
        });

        if (returnFocusRef.current) {
            setTimeout(() => {
                returnFocusRef.current.focus();
            }, 100);
        }
    };

    const buildFacetSearchURL = (searchURL, ajaxLoadURL, segmentValue) => {
        let ajaxKeyword = searchURL.indexOf('ajaxsearch?') > -1 ? 'ajaxsearch?' : 'ajaxsearch';
        let sliceIndex = searchURL.indexOf(ajaxKeyword) + ajaxKeyword.length;
        let currentQueryString = searchURL.slice(sliceIndex).length > 0 ? '&' + searchURL.slice(sliceIndex) : '';
        let searchUrl = ('' + ajaxLoadURL + currentQueryString)
        if (props.ClassificationToForceScope) {
            searchUrl += '&ScopeCategoryIDs=' + props.ClassificationToForceScope;
        }
        if (segmentValue && !isNaN(segmentValue)) {
            searchUrl += "&SegmentValue=" + segmentValue;
        }
        return searchUrl;
    };

    const loadAllOptionsForFacet = (isLoaded, showModal, facets, allFacets, filter, segmentValue) => {
        if (isLoaded && !segmentValue || !props.LoadAsync) {
            loadOptions(facets, allFacets, filter);
        } else if (props.LoadAsync) {
            let url = buildFacetSearchURL(props.SearchURL, props.AjaxFacetLoadURL, segmentValue);
            if (isCheckbox) {
                sandhillsRequest({
                    url: url,
                    errorHandling: {
                        message: 'An error occured while retriving faceted options. Please refresh your page and try again.',
                        useConsole: true,
                        useAlert: true
                    }
                }).then((data) => {
                    let facets = [...options];
                    let allFacets = [...allOptions];
                    let filter = false;
                    if (data) {
                        if (Array.isArray(data) && data.length > 0) {
                            facets = data;
                        } else if (data['SelectedOptionTree'] && data['SelectedOptionTree'].length > 0) {
                            facets = data['SelectedOptionTree'];
                            filter = true;
                            if (data['FullOptionTree'] && data['FullOptionTree'].length > 0) {
                                allFacets = data['FullOptionTree'];
                            }
                        }
                    }

                    let shouldExpand = true;
                    if (!props.OpenFieldOnLoad && data.length > isShortListCount) {
                        shouldExpand = false;
                    }

                    setIsLoaded(!segmentValue);
                    setIsOpen(shouldExpand);
                    setOptions(facets);
                    setAllOptions(allFacets);
                    setFilterOptions(filter);
                    if (showModal) {
                        loadOptions(facets, allFacets, filter);
                    } else if (!shouldExpand) {
                        openShowAllModal(true, false, facets, allFacets, filter);
                    }
                });
            } else if (props.Type === 'Range') {
                sandhillsRequest({
                    url: url,
                    errorHandling: {
                        message: 'An error occured while retriving faceted options. Please refresh your page and try again.',
                        useConsole: true,
                        useAlert: true
                    }
                }).then((data) => {
                    setIsLoaded(true);
                    setRangeInfo(data);
                });
            }
        }
    };

    const searchDispatcher = (e) => {
        if (e.data.searchResult && e.data.nestedTreeResult) {
            props.DispatchFacetedSearchModal({
                type: 'update',
                payload: {
                    SearchResult: e.data.searchResult,
                    NestedTree: e.data.nestedTreeResult
                }
            });
        }
    };

    const loadOptions = (facets, allFacets, filterFacets) => {
        if (props.setShowLoadingRing) props.setShowLoadingRing(false);
        if (isCheckbox) {
            props.DispatchFacetedSearchModal({
                type: 'update',
                payload: {
                    ...props,
                    Options: facets,
                    AllOptionsLoaded: true,
                    ShowLoadingSpinner: false,
                    RunSearch: runFacetedSearch,
                    CloseModal: (applyFilter, resetOptions) => closeShowAllModal(applyFilter, resetOptions, allFacets),
                    IsShortListCount: isShortListCount,
                    LoadFullOptionTree: (facets) => loadAllOptions(facets, allFacets),
                    FilterOptionsBySelections: filterFacets,
                    SearchDispatcher: searchDispatcher
                }
            });
        }
    };

    const loadAllOptions = (options, allOptions) => {
        let currentOptions = [...allOptions].map(option => {
            let currentOption = options.find(x => x.ReactKey === option.ReactKey);
            let childReactKey = options.find(x => option.ReactKey === (option.ParentName + option.ParentValue + x.ReactKey));
            if (currentOption) {
                return { ...currentOption, Indent: option.Indent };
            } else if (childReactKey) {
                return { ...option, Checked: childReactKey.Checked };
            } else {
                return { ...option };
            }
        });
        setOptions(currentOptions);
        setFilterOptions(false);
        props.DispatchFacetedSearchModal({
            type: 'update',
            payload: {
                Options: currentOptions,
                FilterOptionsBySelections: false
            }
        });
    };

    return ((props.FacetClosed === true && props.LoadAsync && isCheckbox && !props.SegmentOverhaul) || (isCheckbox && windowSize.isMobile && props.HasSingleSelectionOption) ?
        <div className={'faceted-search-field ' + props.Type + (props.IsEmptySearch ? ' disabled-facet' : '')}>
            <button ref={returnFocusRef} className={'ts-button faceted-section-head section-head' + (props.ClassName ? + ' ' + props.ClassName : + '')} custom-data-field={'cdf-' + props.FieldName} onClick={() => openShowAllModal(isLoaded, false, options, allOptions, filterOptions)}>
                <div className='faceted-section-title'>
                    <ConditionalWrapper condition={props.UseSEOHTagReorganization} wrapper={children => <h3 className="header-reset">{children}</h3>}>
                        <span dangerouslySetInnerHTML={{ __html: props.DisplayName }}></span>
                        {displayAppliedText()}
                    </ConditionalWrapper>                    
                </div>
                <span>
                    <i className='fas fa-chevron-right' />
                </span>
            </button>
        </div>
        :
        <div className={'faceted-search-field ' + props.Type + (props.IsEmptySearch && !props.KeepFacetEnabled  ? ' disabled-facet' : '')}>
            <button className={'ts-button faceted-section-head section-head ' + (props.ClassName ? ' ' + props.ClassName : '')} custom-data-field={'cdf-' + props.FieldName} aria-expanded={isAriaExpanded()} onClick={() => toggleOpenClose(false)}>
                <div className='faceted-section-title'>
                    <ConditionalWrapper condition={props.UseSEOHTagReorganization} wrapper={children => <h3 className="header-reset">{children}</h3>}>
                        <span dangerouslySetInnerHTML={{ __html: props.DisplayName }}></span>
                        {displayAppliedText()}
                    </ConditionalWrapper>

                    {props.ShowEmployeeOnlyInfo && props.FieldName == 'keywords' && props.KeywordSearchParamsDetected &&
                        <a className='open-spec-link' role='button' onClick={toggleEmployeeModal}>
                            <i className='fas fa-external-link-alt'></i>
                        </a>}                    
                </div>
                {props.Collapsible &&
                    <i className={getIconClass()} />
                }
            </button>

            {showEmployeeKeywordModal &&
                <EmployeeKeywordSearchParamsModal
                    KeywordSearchParamsDetected={props.KeywordSearchParamsDetected}
                    toggleEmployeeModal={toggleEmployeeModal}
                />}

            <fieldset className="ts__fieldset">
                <legend className='visually-hidden ts__legend'>{props.FieldName}</legend>
                <div className={getFacetedSectionBoxClass()} key={props.FieldName} > 
                    {((isOpen && props.FacetClosed !== true) || props.SegmentOverhaul) && renderFields()}
                </div>
            </fieldset>            
        </div>
    );
};

export default FacetedSearchField;